import 'react-datepicker/dist/react-datepicker.css';

import { css } from '@emotion/react';
import { DateTime } from 'luxon';
import { ElementRef, ReactNode, useEffect, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { DateRange, isDateInList, isDateInRange, isInRange, isSameDate, isSameMonth, isTruthy } from 'shared';

import { DateInput } from '../forms/ui';
import { SasIcon } from '../SasIcon';
import { theme } from '../theme';

import { DatepickerStylesOverride } from './OverrideStyles';

const styles = {
  topContent: css`
    display: flex;
    gap: ${theme.spacing.md};
    justify-content: space-between;
    align-items: center;
    padding-bottom: ${theme.spacing.lg};
    border-bottom: 1px solid ${theme.colors.border};
    margin-bottom: ${theme.spacing.lg};

    & > h2 {
      margin-bottom: 0;
    }
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${theme.spacing.sm};
  `,
  headerContent: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 0;
  `,
  headerLabel: css`
    font-family: 'scandinavian-new', 'scandinavian', sans-serif;
    font-size: ${theme.fontSize.lg};
    font-weight: ${theme.fontWeight.bold};
    line-height: ${theme.spacing.lg};
    margin-bottom: 0;
    color: ${theme.colors.blue[3]};
    text-transform: capitalize;
  `,
  navButtonContainer: css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
  `,

  inputContainer: css`
    width: 65%;
    max-width: 100%;
  `,
};

type Selected = { primary?: DateTime; secondary?: DateTime; emphasized?: 'primary' | 'both' };

type CalendarEntry = Array<DateRange | DateTime> | DateTime | DateRange;

export type DayInfo = Record<string, ReactNode>;

type DatePickerProps = {
  title: string;
  selectable?: DateRange;
  selected: Selected;
  highlighted?: CalendarEntry;
  dayInfo?: DayInfo;
  showDateInput?: boolean;
  handleSelect: (date: DateTime) => void;
  handleDisplayedMonthChange?: (date: DateTime) => void;
  className?: string;
  displayedDate?: DateTime;
};

export function DatePicker(props: DatePickerProps) {
  const { title, displayedDate, selectable, dayInfo, showDateInput, className } = props;

  const primarySelected = props.selected.primary;

  const fromDate = selectable?.from ? selectable.from.toJSDate() : displayedDate?.toJSDate();

  const openToDate = primarySelected ? primarySelected.toJSDate() : fromDate;

  const selectedRange = getSelectedRange(props.selected);

  const highlighted = getDateTimesFromEntries(props.highlighted);

  const selectedRef = useRef(primarySelected);

  const emphasizedSelectedStart = props.selected.emphasized === 'both' || selectedRange?.from === primarySelected;
  const emphasizedSelectedEnd = props.selected.emphasized === 'both' || selectedRange?.to === primarySelected;

  const datePickerRef = useRef<ElementRef<'div'>>(null);

  useEffect(() => {
    // We don't want the date picker to be tabbable but react-datepicker will append tabindex to each day element,
    // so we need to programmatically remove them.
    datePickerRef.current?.querySelectorAll('.react-datepicker__day').forEach(element => {
      if (element instanceof HTMLElement) {
        element.removeAttribute('tabindex');
      }
    });
  });

  useEffect(() => {
    if (props.handleDisplayedMonthChange && primarySelected && !isSameMonth(primarySelected, selectedRef.current)) {
      props.handleDisplayedMonthChange(primarySelected);
    }

    selectedRef.current = primarySelected;
  }, [primarySelected]);

  const isSelectable = (date: Date) => {
    const d = DateTime.fromJSDate(date).startOf('day');

    return selectable ? isInRange({ date: d, range: selectable }) : false;
  };

  const getDayClass = (date: Date) => {
    const d = DateTime.fromJSDate(date).startOf('day');

    const classes = [];

    if (selectedRange && isDateInRange(selectedRange, d)) {
      classes.push('selected');

      if (isSameDate(d, selectedRange.from)) {
        classes.push('selected-start', emphasizedSelectedStart && 'selected-emphasized');
      }

      if (isSameDate(d, selectedRange.to)) {
        classes.push('selected-end', emphasizedSelectedEnd && 'selected-emphasized');
      }
    }

    classes.push(isDateInList(highlighted, d) && 'highlighted');

    return classes
      .filter(isTruthy)
      .map(c => `react-datepicker__day--${c}`)
      .join(' ');
  };

  return (
    <div className={className}>
      <div className="sas-datepicker-portal" ref={datePickerRef}>
        <DatepickerStylesOverride />

        <ReactDatePicker
          disabledKeyboardNavigation={true}
          onMonthChange={date => props.handleDisplayedMonthChange?.(DateTime.fromJSDate(date))}
          renderCustomHeader={params => {
            const d = DateTime.fromJSDate(params.date).startOf('month').startOf('day');

            let showPrevButton = true;
            let showNextButton = true;

            if (selectable) {
              const lowerBound = selectable.from.startOf('month').startOf('day');
              const upperBound = selectable.to.startOf('month').startOf('day');

              showPrevButton = d > lowerBound;
              showNextButton = d < upperBound;
            }

            return (
              <>
                <div css={styles.topContent}>
                  <h2>{title}</h2>
                  {showDateInput && (
                    <div css={styles.inputContainer}>
                      <DateInput
                        selected={primarySelected}
                        title={title}
                        selectable={selectable}
                        onSelect={props.handleSelect}
                        showIcon
                      />
                    </div>
                  )}
                </div>
                <div css={styles.header}>
                  <div css={styles.navButtonContainer}>
                    <button
                      type="button"
                      className={`react-datepicker__arrow-button ${!showPrevButton ? 'disabled' : ''}`}
                      aria-disabled={!showPrevButton}
                      disabled={!showPrevButton}
                      tabIndex={-1}
                      onClick={() => {
                        params.decreaseMonth();
                      }}
                    >
                      <SasIcon name="in--arrow-left" color={theme.colors.white} />
                    </button>
                  </div>

                  <h5 css={styles.headerContent}>
                    <span css={styles.headerLabel}>{d.toFormat('MMMM yyyy')}</span>
                  </h5>

                  <div css={styles.navButtonContainer}>
                    <button
                      type="button"
                      className={`react-datepicker__arrow-button ${!showNextButton ? 'disabled' : ''}`}
                      aria-disabled={!showNextButton}
                      disabled={!showNextButton}
                      tabIndex={-1}
                      onClick={() => {
                        params.increaseMonth();
                      }}
                    >
                      <SasIcon name="in--arrow-right" color={theme.colors.white} />
                    </button>
                  </div>
                </div>
              </>
            );
          }}
          filterDate={isSelectable}
          dayClassName={getDayClass}
          onChange={date => {
            if (date) {
              props.handleSelect(DateTime.fromJSDate(date));
            }
          }}
          openToDate={openToDate}
          selected={primarySelected ? primarySelected.toJSDate() : null}
          inline
          fixedHeight
          calendarStartDay={1}
          formatWeekDay={value => {
            const monday = DateTime.now().startOf('week');

            return monday.plus({ days: dayOfWeek[value] }).toFormat('ccc');
          }}
          renderDayContents={(day, date) => {
            const dateTime = date && DateTime.fromJSDate(date);
            const className =
              dateTime && isDateInList(highlighted, dateTime) ? 'react-datepicker__highlighted-box' : '';

            return (
              <div className={className}>
                {day}
                {dayInfo && dateTime && (
                  <div className="react-datepicker__day-info">{dayInfo[dateTime.toFormat('yyyy-MM-dd')]}</div>
                )}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}

function getSelectedRange(selected: Selected): DateRange | undefined {
  if (!selected.primary) {
    return;
  }

  if (!selected.secondary) {
    return { from: selected.primary, to: selected.primary };
  }

  return selected.primary > selected.secondary
    ? { from: selected.secondary, to: selected.primary }
    : { from: selected.primary, to: selected.secondary };
}

function getDateTimesFromEntries(entries: CalendarEntry | undefined): DateTime[] {
  if (!entries) return [];

  const entryList = Array.isArray(entries) ? entries : [entries];

  return entryList.flatMap(entry => {
    if (DateTime.isDateTime(entry)) {
      return [entry.startOf('day')];
    }

    const range: DateTime[] = [];
    let current = entry.from.startOf('day');
    const end = entry.to.startOf('day');

    while (current <= end) {
      range.push(current);
      current = current.plus({ days: 1 });
    }
    return range;
  });
}

const dayOfWeek: Record<string, number> = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
  Saturday: 5,
  Sunday: 6,
};
